import { useState, useEffect, useRef } from "react";
import { Button, DatePicker, Image, Input, Modal, Space, Tag } from "antd";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { acceptOrRejectIDImages, getPendingNationalIds } from "Network";
import { toastError, toastSuccess } from "helpers/toasters";
import { createBackgroundImage } from "helpers/createBackgoundImageUrl";
import { handleAllowNumbersOnly } from "helpers/helpfulFunctions";

import GridTable from "modules/SubComponents/GridTable/GridTable";
import Header from "modules/SubComponents/Header";
import SubHeader from "modules/SubComponents/SubHeader";

dayjs.extend(customParseFormat);

const STATUS_FILTERS = [
  { text: "Pending", value: "Pending" },
  { text: "Accepted", value: "Accepted" },
  { text: "Rejected", value: "Rejected" },
];

const parseCreationDate = (dateStr) => {
  if (!dateStr) return null;
  const parsed = dayjs(dateStr, "DD MMM, YYYY", true);
  return parsed.isValid() ? parsed.startOf("day") : null;
};

function PendingNationalIds() {
  const [loading, setLoading] = useState(true);
  const [allData, setAllData] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [rowData, setRowData] = useState({});
  const [render, setRender] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isApprove, setIsApprove] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [searchValues, setSearchValues] = useState({
    fullName: "",
    mobile: "",
    idNumberImagestatus: "",
    from: "",
    to: "",
  });
  const [selectedRange, setSelectedRange] = useState([]);
  const searchInput = useRef(null);
  const { RangePicker } = DatePicker;

  const updateSearchValues = (property, value, otherProperty) => {
    setSearchValues((prev) => {
      const next = { ...prev, [property]: value };
      if (otherProperty) {
        next[otherProperty.property] = otherProperty.value;
      }
      return next;
    });
  };

  const applyFilters = (data, filters = searchValues) => {
    return data.filter((row) => {
      if (
        filters.fullName &&
        !row.fullName?.toLowerCase().includes(filters.fullName.toLowerCase())
      ) {
        return false;
      }
      if (
        filters.mobile &&
        !String(row.mobile || "").includes(filters.mobile)
      ) {
        return false;
      }
      if (
        filters.idNumberImagestatus &&
        row.idNumberImagestatus !== filters.idNumberImagestatus
      ) {
        return false;
      }
      if (filters.from || filters.to) {
        const rowDate = parseCreationDate(row.creationDate);
        if (!rowDate) return false;
        if (filters.from) {
          const start = dayjs(filters.from).startOf("day");
          if (rowDate.isBefore(start, "day")) return false;
        }
        if (filters.to) {
          const end = dayjs(filters.to).startOf("day");
          if (rowDate.isAfter(end, "day")) return false;
        }
      }
      return true;
    });
  };

  const handleDateChange = (dates) => {
    setSelectedRange(dates);
    if (dates) {
      updateSearchValues("from", dates[0].format("YYYY-MM-DD"), {
        property: "to",
        value: dates[1].format("YYYY-MM-DD"),
      });
    } else {
      updateSearchValues("from", "", { property: "to", value: "" });
    }
  };

  const fetchPendingNationalIds = () => {
    setLoading(true);
    getPendingNationalIds(
      (res) => {
        setLoading(false);
        if (res?.success) {
          const list = res.data || [];
          setAllData(list);
          setDataSource(applyFilters(list, searchValues));
        } else {
          toastError(res?.message);
        }
      },
      (fail) => {
        setLoading(false);
        toastError(fail?.message || fail?.data?.error);
      },
    );
  };

  useEffect(() => {
    fetchPendingNationalIds();
  }, [render]);

  useEffect(() => {
    setDataSource(applyFilters(allData));
  }, [searchValues, allData]);

  const handleTableChange = (_, filters) => {
    if (filters.idNumberImagestatus?.length) {
      if (filters.idNumberImagestatus.length > 1) {
        updateSearchValues("idNumberImagestatus", "");
      } else {
        updateSearchValues(
          "idNumberImagestatus",
          filters.idNumberImagestatus[0],
        );
      }
    } else if (searchValues.idNumberImagestatus) {
      updateSearchValues("idNumberImagestatus", "");
    }
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    const value = selectedKeys[0] || "";
    if (dataIndex === "fullName") {
      updateSearchValues("fullName", value);
    } else if (dataIndex === "mobile") {
      updateSearchValues("mobile", value);
    }
    confirm();
  };

  const handleReset = (clearFilters, dataIndex, confirm) => {
    if (dataIndex === "fullName") {
      updateSearchValues("fullName", "");
    } else if (dataIndex === "mobile") {
      updateSearchValues("mobile", "");
    }
    clearFilters();
    confirm();
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onKeyDown={(e) => {
            if (dataIndex === "mobile") {
              handleAllowNumbersOnly(e, true);
            }
          }}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() =>
              clearFilters && handleReset(clearFilters, dataIndex, confirm)
            }
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          {/* <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button> */}
          <Button type="link" size="small" onClick={() => close()}>
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    filteredValue: searchValues[dataIndex] ? [searchValues[dataIndex]] : null,
    onFilter: () => true,
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const getColumnDateProps = () => ({
    filterDropdown: () => (
      <div
        style={{ padding: 8 }}
        className="range-picker-holder"
        onKeyDown={(e) => e.stopPropagation()}
      >
        <RangePicker
          value={selectedRange}
          allowClear
          popupClassName="all-orders-date-picker"
          inputReadOnly={false}
          onChange={handleDateChange}
          onKeyDown={(e) => e.preventDefault()}
        />
      </div>
    ),
    filterIcon: () => (
      <FilterOutlined
        style={{ color: searchValues.from ? "#1890ff" : undefined }}
      />
    ),
    onFilter: () => true,
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) => text,
  });

  const handleAcceptBtn = (row) => {
    setIsApprove(true);
    setRowData(row);
    setIsConfirmModalOpen(true);
  };

  const handleRejectBtn = (row) => {
    setIsApprove(false);
    setRowData(row);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmCancel = () => {
    setIsConfirmModalOpen(false);
    setRowData({});
  };

  const handleConfirmOk = () => {
    const payload = {
      id: rowData?.userId,
      isAccepted: isApprove,
    };

    acceptOrRejectIDImages(
      payload,
      (res) => {
        if (res?.success) {
          toastSuccess(res.message);
          setRender((prev) => !prev);
        } else {
          toastError(res.message);
        }
      },
      (fail) => {
        toastError(fail?.message || fail?.data?.error);
      },
    );
    setIsConfirmModalOpen(false);
    setRowData({});
  };

  const columns = [
    {
      key: "fullName",
      title: "NAME",
      dataIndex: "fullName",
      ...getColumnSearchProps("fullName"),
    },
    {
      key: "mobile",
      title: "MOBILE NUMBER",
      dataIndex: "mobile",
      ...getColumnSearchProps("mobile"),
    },
    {
      key: "idNumberImage",
      title: "ID IMAGE",
      dataIndex: "idNumberImage",
      render: (idNumberImage) => {
        if (!idNumberImage) return "—";
        const src = createBackgroundImage(idNumberImage);
        return (
          <Image
            src={src}
            alt="National ID"
            width={80}
            height={50}
            style={{ objectFit: "cover", cursor: "pointer" }}
            preview={{ src }}
          />
        );
      },
    },
    {
      key: "creationDate",
      title: "CREATION DATE",
      dataIndex: "creationDate",
      ...getColumnDateProps(),
    },
    {
      key: "idNumberImagestatus",
      title: "STATUS",
      dataIndex: "idNumberImagestatus",
      filters: STATUS_FILTERS,
      filteredValue: searchValues.idNumberImagestatus
        ? [searchValues.idNumberImagestatus]
        : null,
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: () => true,
      render: (status) => {
        const colors = {
          Accepted: "green",
          Rejected: "red",
          Pending: "gold",
        };
        return (
          <Tag
            style={{ width: 100, textAlign: "center" }}
            color={colors[status]}
          >
            {status}
          </Tag>
        );
      },
    },
    {
      key: "salesPerson",
      title: "SALES PERSON",
      dataIndex: "salesPerson",
      render: (salesPerson) => {
        return <span>{salesPerson || "N/A"}</span>;
      },
    },
    {
      key: "userId",
      title: "ACTIONS",
      dataIndex: "action",
      render: (_, row) => (
        <Space size={5} wrap>
          <Button type="primary" onClick={() => handleAcceptBtn(row)}>
            Accept
          </Button>
          <Button danger onClick={() => handleRejectBtn(row)}>
            Reject
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <section className="users">
      <div className="users__all-contents">
        <div className="users__content">
          <div className="users__usersData">
            <Header title="Pending National ID" />
            <SubHeader bgColor="#356ab3" title="All Pending National ID" />
            <div className="users__table grid-table mrg-ye-50 ">
              <GridTable
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                rowId="userId"
                title="Pending Users"
                onChange={handleTableChange}
              />
            </div>
          </div>
        </div>
      </div>
      {isConfirmModalOpen && (
        <Modal
          title={isApprove ? "Accept National ID" : "Reject National ID"}
          open={isConfirmModalOpen}
          onOk={handleConfirmOk}
          onCancel={handleConfirmCancel}
          centered
        >
          <span>{`Are you sure you want to ${
            isApprove ? "accept" : "reject"
          } this user's national ID?`}</span>
        </Modal>
      )}
    </section>
  );
}

export default PendingNationalIds;
